import { useCallback, useState } from 'react'
import dayjs from 'dayjs'
import { generateAgamiMediaSignedUrl, singleDbUpsert } from '../utils/helpers/supabase.helpers'
import { GEOXP_SPOTS_TABLE } from '../utils/constants/db.constants'
import { useFeatureAuth } from './authorization'
import { FEATURES } from '../utils/constants/features.constants'

interface UseAgamiMediaSignedUrlGenerateParams {
  agamiId: string
  mediaType: number
  onSuccess?: (data: { signedUrl: string; signedUrlExpiresAt: string }) => void
  onError?: (error: string | Error | Record<string, unknown> | unknown) => void
}

interface GenerateUrlParams {
  mediaPath: string
  expiresIn: number
}

interface UseAgamiMediaSignedUrlGenerateReturn {
  generateUrl: (params: GenerateUrlParams) => Promise<void>
  isLoading: boolean
}

export const useAgamiMediaSignedUrlGenerate = ({
  agamiId,
  mediaType,
  onSuccess,
  onError,
}: UseAgamiMediaSignedUrlGenerateParams): UseAgamiMediaSignedUrlGenerateReturn => {
  const experimentalFeats = useFeatureAuth(FEATURES.EXPERIMENTAL_FEATURES)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const manageError = useCallback(
    (error: string | Error | Record<string, unknown> | unknown) => {
      setIsLoading(false)

      if (onError) {
        onError(error)
      }
    },
    [onError]
  )

  const generateUrl = useCallback(
    async ({ mediaPath, expiresIn }: GenerateUrlParams): Promise<void> => {
      try {
        if (!experimentalFeats) {
          manageError('Operation not allowed')
          return
        }

        if (!agamiId) {
          manageError('Invalid agami ID')
          return
        }

        setIsLoading(true)

        // get signed URL with desired duration
        const urlGenerationResult = await generateAgamiMediaSignedUrl({
          mediaType,
          mediaPath,
          expiresIn,
        })

        if ('error' in urlGenerationResult && urlGenerationResult.error) {
          manageError(urlGenerationResult.error)
          return
        }

        const { signedUrl } = (urlGenerationResult as { data: { signedUrl: string } }).data

        // calculate audio_signed_url_expires_at timestamp (UTC)
        const signedUrlExpiresAt = dayjs().add(expiresIn, 'seconds').toISOString()

        // upsert DB record to save URL and expiration date
        const { error: upsertError } = await singleDbUpsert({
          table: GEOXP_SPOTS_TABLE,
          upsertData: {
            id: agamiId,
            audio_signed_url: signedUrl,
            audio_signed_url_expires_at: signedUrlExpiresAt,
          },
        })

        if (upsertError) {
          manageError(upsertError)
          return
        }

        setIsLoading(false)

        if (onSuccess) {
          onSuccess({
            signedUrl: signedUrl!,
            signedUrlExpiresAt,
          })
        }
      } catch (err) {
        manageError(err)
      }
    },
    [experimentalFeats, agamiId, manageError, mediaType, onSuccess]
  )

  return {
    generateUrl,
    isLoading,
  }
}
